import React, { useEffect, useState } from "react";
import { Sparkles, Link2, X, CheckCircle2, AlertTriangle, UserX, Wallet, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const rupees = (p) => `₹${((p || 0) / 100).toLocaleString("en-IN")}`;
const CONF = { high: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-800", low: "bg-slate-200 text-slate-700" };

const TABS = [
  { key: "suggestions", label: "Likely same person", Icon: Sparkles },
  { key: "paid_not_marked", label: "Paid, not marked paid", Icon: Wallet },
  { key: "marked_paid_no_payment", label: "Marked paid, no payment", Icon: AlertTriangle },
  { key: "paid_not_allotted", label: "Paid, not allotted", Icon: UserX },
  { key: "unpaid_no_payment", label: "Allotted, not paid", Icon: X },
];

export default function SmartReconcileModal({ event, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("suggestions");
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(new Set());

  const load = async () => {
    setData(null); setDone(new Set());
    try { const r = await api.get(`/events/${event.event_id}/smart-reconcile`); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not run reconcile"); onClose(); }
  };
  useEffect(() => { load(); }, []);

  const act = async (rowId, body, msg) => {
    setBusy(rowId);
    try {
      await api.post(`/events/${event.event_id}/smart-reconcile/apply`, body);
      setDone((d) => new Set(d).add(rowId));
      toast.success(msg);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setBusy("");
  };

  const rows = data ? data[tab] : [];
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="smart-reconcile-modal">
        <div className="p-6 pb-3 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h4 className="font-display font-bold text-xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-orange-500" /> Smart reconcile — payments vs allotments</h4>
            <div className="flex gap-2">
              <button onClick={load} className="btn-ghost text-xs py-1" data-testid="smart-reconcile-rerun"><RefreshCw className="w-3.5 h-3.5" /> Re-run</button>
              <button onClick={onClose} className="btn-ghost text-xs py-1" data-testid="smart-reconcile-close">Close</button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-1">Checks every receipt and paid form (including each person in bulk registrations) against allotments. Matches emails and phone numbers, and spots names written differently: typos, word order, missing middle names, initials, spelling variants. Nothing changes until you approve it.</p>
          {data && (
            <div className="flex flex-wrap gap-1.5 mt-4" data-testid="smart-reconcile-tabs">
              <span className="text-xs rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700 font-semibold" data-testid="smart-reconcile-matched">✓ {data.summary.matched} already matched</span>
              {TABS.map(({ key, label, Icon }) => (
                <button key={key} onClick={() => setTab(key)} data-testid={`smart-tab-${key}`}
                  className={`text-xs rounded-full px-3 py-1 font-semibold inline-flex items-center gap-1 transition-colors ${tab === key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  <Icon className="w-3.5 h-3.5" /> {label} ({data.summary[key]})
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-3">
          {!data && <div className="text-center py-12 text-slate-400 text-sm" data-testid="smart-reconcile-loading">Analysing names, emails and phone numbers…</div>}
          {data && rows.length === 0 && <div className="text-center py-12 text-emerald-600 font-semibold text-sm" data-testid="smart-reconcile-empty"><CheckCircle2 className="w-6 h-6 mx-auto mb-2" />Nothing to fix here.</div>}
          {data && rows.map((r, i) => <Row key={i} tab={tab} r={r} done={done} busy={busy} act={act} />)}
        </div>
      </div>
    </div>
  );
}

function Row({ tab, r, done, busy, act }) {
  const a = r.allotment || (tab === "marked_paid_no_payment" || tab === "unpaid_no_payment" ? r : null);
  const p = r.payee;
  const id = `${a?.allotment_id || ""}|${p?.key || ""}`;
  const isDone = done.has(id);
  return (
    <div className={`border rounded-xl p-3 ${isDone ? "opacity-50 bg-slate-50" : "border-slate-200"}`} data-testid={`smart-row-${tab}-${a?.allotment_id || p?.key}`}>
      <div className="grid md:grid-cols-2 gap-3">
        {p && <Person title="Payment record" name={p.raw_name} lines={[p.email, p.phone, p.school, `${p.ref || ""} · ${rupees(p.amount_paise)}${p.is_bulk ? " · bulk" : ""}`]} tone="emerald" />}
        {a && <Person title="Allotment" name={a.raw_name} lines={[a.email, a.school, `${a.committee} · ${a.portfolio}`, a.paid ? "Marked paid" : "Marked unpaid"]} tone="indigo" />}
        {tab === "paid_not_allotted" && r.closest && (
          <div className="text-xs text-slate-500 self-center">Closest allotment: <b>{r.closest.allotment.raw_name}</b> ({r.closest.score}% — too different to suggest)</div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {r.score != null && <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${CONF[r.confidence]}`} data-testid="smart-row-score">{r.score}% · {r.confidence}</span>}
        {(r.reasons || (r.how ? [r.how] : [])).map((x) => <span key={x} className="text-xs rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">{x}</span>)}
        <div className="ml-auto flex gap-2">
          {isDone ? <span className="text-xs font-semibold text-emerald-700">✓ Done</span> : <Actions tab={tab} a={a} p={p} id={id} busy={busy} act={act} />}
        </div>
      </div>
    </div>
  );
}

function Actions({ tab, a, p, id, busy, act }) {
  const dis = busy === id;
  if (tab === "suggestions") return (<>
    <button disabled={dis} onClick={() => act(id, { action: "ignore", allotment_id: a.allotment_id, payee_key: p.key }, "Ignored — won't be suggested again")} className="btn-ghost text-xs py-1" data-testid="smart-ignore-btn">Not the same</button>
    <button disabled={dis} onClick={() => act(id, { action: "link", allotment_id: a.allotment_id, payee_key: p.key }, `Linked ${a.raw_name} → marked paid`)} className="btn-primary text-xs py-1" data-testid="smart-link-btn"><Link2 className="w-3.5 h-3.5" /> Same person — link & mark paid</button>
  </>);
  if (tab === "paid_not_marked") return <button disabled={dis} onClick={() => act(id, { action: "link", allotment_id: a.allotment_id, payee_key: p.key }, `${a.raw_name} marked paid`)} className="btn-primary text-xs py-1" data-testid="smart-mark-paid-btn">Mark paid</button>;
  if (tab === "marked_paid_no_payment") return <button disabled={dis} onClick={() => window.confirm(`Mark ${a.raw_name} as UNPAID? No receipt or paid form was found for them.`) && act(id, { action: "mark_unpaid", allotment_id: a.allotment_id }, `${a.raw_name} marked unpaid`)} className="btn-ghost text-xs py-1 text-rose-600 hover:bg-rose-50" data-testid="smart-mark-unpaid-btn">Mark unpaid</button>;
  return null;
}

function Person({ title, name, lines, tone }) {
  return (
    <div className={`rounded-lg p-2.5 ${tone === "emerald" ? "bg-emerald-50/60" : "bg-indigo-50/60"}`}>
      <div className={`text-[10px] uppercase font-bold tracking-wide ${tone === "emerald" ? "text-emerald-700" : "text-indigo-700"}`}>{title}</div>
      <div className="font-semibold text-slate-900">{name || "—"}</div>
      {lines.filter(Boolean).map((l, i) => <div key={i} className="text-xs text-slate-500 truncate">{l}</div>)}
    </div>
  );
}
